import {Component, inject, OnInit, signal} from '@angular/core';
import {RouterOutlet} from '@angular/router';
import {EMPTY} from 'rxjs';
import {catchError, filter, switchMap} from 'rxjs/operators';
import {ServiceWorkerService, ServiceWorkerEvent} from '@xmedical/core';
import {XmedicalUiModule} from '@xmedical/ui';
import {ToolbarComponent} from './core/layout/toolbar/toolbar.component';
import {AuthenticationService} from './core/auth/authentication.service';
import {OrganisationStore} from './core/stores/organisation.store';
import {ThemeService} from './core/services/theme.service';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, ToolbarComponent, XmedicalUiModule],
  templateUrl: './app.html',
  styleUrl: './app.scss'
})
export class App implements OnInit {
  private authService = inject(AuthenticationService);
  private orgStore = inject(OrganisationStore);
  private themeService = inject(ThemeService);
  private pwaService = inject(ServiceWorkerService);

  readonly updateAlertVisible = signal(false);

  get isAuthenticated(): boolean {
    return this.authService.isAuthenticated();
  }

  ngOnInit(): void {
    this.themeService.init();

    this.pwaService.event$.subscribe((event: ServiceWorkerEvent) => {
      if (event === 'update_available') {
        this.updateAlertVisible.set(true);
      }
    });

    // Cold boot org init runs in the APP_INITIALIZER (app.config.ts). This
    // subscription only handles the *re-login* case: a logged-out user logs
    // back in within the same browser session, in which case we need to
    // re-run org initialisation now that auth is back.
    this.authService.events$.pipe(
      filter(event => event.type === 'login'),
      // Trap initialise() errors inside the inner pipeline so a failed
      // re-login init doesn't tear down the outer events$ subscription.
      // Without this, one bad init would prevent every subsequent login
      // event in the same tab from triggering re-init. OrganisationStore
      // already logs the error itself.
      switchMap(() => this.orgStore.initialise().pipe(catchError(() => EMPTY))),
    ).subscribe();
  }

  reload(): void {
    window.location.reload();
  }
}
